import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Loading from "examples/Loading";
import TabletMap from "./TabletMap";
import CrudService from "services/cruds-service";
import moment from "moment";
import TankVisual from "../TankVisual";
import ChannelGauge from "./ChannelGauge";
import SensorsOffIcon from "@mui/icons-material/SensorsOff";
import { useWebSocket } from "context/WebSocketContext";
import TabletWorkspace from "./TabletWorkspace";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useTheme } from "@mui/material/styles";
import LiveTransferCard from "./transfer/LiveTransferCard";
import TransferMonitorDialog from "./transfer/TransferMonitorDialog";
import useTransferSession from "./transfer/useTransferSession";
import useTransferDay from "./transfer/useTransferDay";

const LIVE_WINDOW_MINUTES = 3;

const MOTION_COLOR = {
  moving: "success",
  parked: "default",
  idle: "warning",
  unknown: "default",
};

const PTO_CAN_KEYS = [
  "ptoAnyEngaged",
  "ptoHours",
  "ptoActiveTelltale",
  "ptoCabControlTelltale",
  "ptoRemoteControlTelltale",
  "ptoModeActiveTelltale",
  "eptoEnableDemand",
  "eptoOperationalStatus",
  "eptoSystemError",
  "eptoDriveEngagement",
];

function formatNumber(value, digits = 0) {
  if (value == null || value === "") return "--";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function getVehicleReading(deviceValue, key) {
  const reading = deviceValue?.vehicle?.readings?.[key];
  if (reading) {
    return {
      label: reading.label || key,
      value: reading.display_value ?? reading.canonical_value ?? reading.value,
      unit: reading.display_unit || reading.canonical_unit || reading.unit || "",
      quality: reading.quality || (reading.stale ? "stale" : "good"),
    };
  }

  const channel = deviceValue?.channels?.[`can_${key}`] ?? deviceValue?.channels?.[key];
  if (channel) {
    return {
      label: channel.channel_name || channel.label || key,
      value: channel.display_value ?? channel.current_value,
      unit: channel.display_unit || channel.canonical_unit || "",
      quality: channel.quality || "good",
    };
  }

  return null;
}

function formatPtoStatus(value) {
  if (value == null) return "--";
  const numericValue = Number(value);
  if (numericValue === 0) return "Off";
  if (numericValue === 1) return "On";
  if (numericValue === 2) return "Error";
  if (numericValue === 3) return "Unavailable";
  return value;
}

const TELEMETRY_ORDER_VERSION = "v1";

function telemetryOrderStorageKey(deviceId) {
  return `tablet:${deviceId}:telemetry-card-order:${TELEMETRY_ORDER_VERSION}`;
}

function readTelemetryOrder(deviceId) {
  try {
    const stored = localStorage.getItem(telemetryOrderStorageKey(deviceId));
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("readTelemetryOrder", error);
    return [];
  }
}

function reorderItems(items, startIndex, endIndex) {
  const nextItems = [...items];
  const [removed] = nextItems.splice(startIndex, 1);
  nextItems.splice(endIndex, 0, removed);
  return nextItems;
}

function applyTelemetryOrder(cards, savedOrder) {
  const rank = new Map(savedOrder.map((cardId, index) => [cardId, index]));
  return [...cards].sort((left, right) => {
    const leftRank = rank.get(left.id) ?? Number.MAX_SAFE_INTEGER;
    const rightRank = rank.get(right.id) ?? Number.MAX_SAFE_INTEGER;
    return leftRank - rightRank;
  });
}

function getRs485Status(deviceData, deviceValue) {
  const diagnosticsRs485 = deviceData?.diagnostics?.rs485 ?? {};
  const rs485 = deviceData?.rs485 ?? deviceValue?.rs485 ?? {};
  const sensor = deviceData?.sensor ?? deviceValue?.sensor ?? {};

  return {
    connectionStatus:
      rs485.connectionStatus ??
      sensor.connectionStatus ??
      diagnosticsRs485.connection_status ??
      diagnosticsRs485.connectionStatus ??
      "unknown",
    port: rs485.port ?? sensor.port ?? diagnosticsRs485.port ?? "--",
    baudRate: rs485.baudRate ?? "--",
    dataBits: rs485.dataBits ?? "--",
    stopBits: rs485.stopBits ?? "--",
    parity: rs485.parity ?? "--",
    activeSlaveAddress: rs485.activeSlaveAddress ?? sensor.slaveAddress ?? "--",
    consecutiveFailures:
      rs485.consecutiveFailures ??
      sensor.consecutiveFailures ??
      diagnosticsRs485.consecutive_failures ??
      0,
    crcValid: rs485.crcValid ?? sensor.crcValid ?? diagnosticsRs485.crc_valid,
    lastSuccessfulAt: rs485.lastSuccessfulAt ?? sensor.lastSuccessfulAt,
    requestHex: rs485.requestHex ?? sensor.requestHex,
    responseHex: rs485.responseHex ?? sensor.responseHex,
    registerReads: sensor.registerReads ?? [],
    pressurePsi: sensor.pressurePsi ?? deviceValue?.pressure_psi,
    pressureMode: sensor.pressureMode ?? deviceValue?.pressure_mode,
  };
}

const FieldRow = ({ label, value, valueColor = "text" }) => (
  <MDBox display="flex" justifyContent="space-between" gap={2}>
    <MDTypography variant="body2" color="text">{label}</MDTypography>
    <MDTypography variant="body2" fontWeight="bold" color={valueColor} textAlign="right">
      {value ?? "--"}
    </MDTypography>
  </MDBox>
);

const MetricCard = ({ icon, label, value, helper, color = "text" }) => (
  <Paper
    elevation={0}
    sx={({ palette, borders, functions: { rgba } }) => ({
      backgroundColor: rgba(palette.text.main, 0.035),
      border: `1px solid ${rgba(palette.text.main, 0.10)}`,
      borderRadius: borders.borderRadius.md,
      height: "100%",
      p: 1.75,
      boxShadow: "none",
    })}
  >
    <MDBox display="flex" justifyContent="space-between" gap={2}>
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {label}
      </MDTypography>
      {icon && (
        <MDBox color={`${color}.main`} lineHeight={1}>
          {icon}
        </MDBox>
      )}
    </MDBox>

    <MDTypography variant="h6" color={color} fontWeight="bold" mt={1}>
      {value}
    </MDTypography>

    {helper && (
      <MDTypography variant="caption" color="text">
        {helper}
      </MDTypography>
    )}
  </Paper>
);

const TabletDevice = () => {
  const { id } = useParams();
  const [device, setDevice] = useState(null);
  const [loader, setLoader] = useState(true);
  const { tabletTelemetryData, jobState, alertData } = useWebSocket();
  const [activeAlertCount, setActiveAlertCount] = useState(0);
  const [currentJobState, setCurrentJobState] = useState(null);
  const [alertColor, setAlertColor] = useState('green')
  const [telemetryOrder, setTelemetryOrder] = useState(() => {
    if (typeof window !== "undefined") {
      return readTelemetryOrder(id);
    }
    return [];
  });
  const theme = useTheme();

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoader(true);
      const res = await CrudService.getDeviceView(id);
      setDevice(res?.data);
      setLoader(false);
    })();
  }, [id]);

  useEffect(() => {
    setTelemetryOrder(readTelemetryOrder(id));
  }, [id]);

  // Merge live telemetry updates
  useEffect(() => {
    if (!id || !tabletTelemetryData) return;
    if (String(tabletTelemetryData.device_id) !== String(id)) return;

    setDevice(prev => {
      if (!prev) return prev;
      
      // Deep comparison to prevent unnecessary re-renders
      const prevValue = prev.latestWebhookData?.device_value;
      const prevData = prev.latestWebhookData?.device_data;
      
      const valueChanged = JSON.stringify(prevValue) !== JSON.stringify(tabletTelemetryData.device_value);
      const dataChanged = JSON.stringify(prevData) !== JSON.stringify(tabletTelemetryData.device_data);

      if (!valueChanged && !dataChanged) return prev;

      return {
        ...prev,
        latestWebhookData: {
          ...prev?.latestWebhookData,
          device_value: tabletTelemetryData.device_value,
          device_data: tabletTelemetryData.device_data,
        },
      };
    });
  }, [id, tabletTelemetryData]);

  const deviceData = device?.latestWebhookData?.device_data ?? device?.attributes?.device_data ?? {};
  const deviceValue = device?.latestWebhookData?.device_value ?? {};
  const channels = deviceValue?.channels ?? {};

  const primaryChannelKey = channels.tank_level ? "tank_level" : null;
  const gridChannelEntries = useMemo(
    () => Object.entries(channels).filter(([key]) => key !== primaryChannelKey && key !== "tank_volume"),
    [channels, primaryChannelKey]
  );

  const ptoReadings = PTO_CAN_KEYS
    .map((key) => [key, getVehicleReading(deviceValue, key)])
    .filter(([, reading]) => reading != null);
  
  const ptoActive = ["ptoAnyEngaged", "ptoActiveTelltale", "eptoDriveEngagement"].some((key) => {
    const reading = getVehicleReading(deviceValue, key);
    return Number(reading?.value) === 1;
  });

  // ----- Active alerts fetching -----
  const addressItemId =
    device?.attributes?.address_item_id ??
    device?.attributes?.EntityAddressItem?.address_item_id ??
    device?.attributes?.entity_address_item?.address_item_id ??
    device?.latestWebhookData?.device_data?.address_item_id ??
    device?.attributes?.device_data?.address_item_id ??
    null;

  const refreshActiveAlerts = useCallback(async () => {
    if (!id) return;
    try {
      const res = await CrudService.getDeviceActiveAlerts(id);
      setActiveAlertCount(res?.data?.alerts?.length ?? 0);
      if(res?.data?.alerts?.length > 0){
        const firstAlert = res?.data?.alerts[0];
        setAlertColor(firstAlert?.BadgeDetail?.background_color_hex || 'green');
      }
    } catch (e) {
      console.error("refreshActiveAlerts", e);
    }
  }, [id]);

  useEffect(() => {
    refreshActiveAlerts();
  }, [refreshActiveAlerts]);

  // Re-fetch when socket sends alertData for this device
  useEffect(() => {
    if (alertData && String(alertData.device_id) === String(id)) {
      refreshActiveAlerts();
    }
  }, [alertData, id, refreshActiveAlerts]);

  // ----- Active job fetching -----
  useEffect(() => {
    if (!addressItemId) return;
    (async () => {
      try {
        const res = await CrudService.getActiveJobForTruck(addressItemId);
        if (res?.success) {
          setCurrentJobState(res?.transitions?.to_state ?? res?.data?.current_state ?? null);
        }
      } catch (e) {
        console.error("fetch active job", e);
      }
    })();
  }, [addressItemId]);

  // Update job state from socket events
  useEffect(() => {
    if (jobState && String(jobState.device_id) === String(id)) {
      setCurrentJobState(jobState.to_state ?? null);
    }
  }, [jobState, id]);

  // ----- Site and map details -----
  const siteItem =
    device?.latestWebhookData?.entity_address_item ??
    device?.attributes?.entity_address_item ??
    device?.attributes?.EntityAddressItem ??
    null;
  const site = siteItem
    ? {
        name:
          siteItem.entity_address_name ||
          siteItem.entity_name ||
          [siteItem.first_name, siteItem.last_name].filter(Boolean).join(" ") ||
          siteItem.item_name ||
          siteItem.Address?.name ||
          "",
        address: [
          siteItem.street_address1 ?? siteItem.Address?.street_address1,
          siteItem.city ?? siteItem.Address?.city,
          siteItem.state ?? siteItem.Address?.state,
          siteItem.postal_code ?? siteItem.Address?.postal_code,
        ]
          .filter(Boolean)
          .join(", "),
        lat: siteItem.latitude ?? siteItem.Address?.latitude,
        lng: siteItem.longitude ?? siteItem.Address?.longitude,
        address_item_id: device?.attributes?.EntityAddressItem?.address_item_id
      }
    : null;

  const telemetryCards = useMemo(
    () => [
      ...gridChannelEntries.map(([key, channel]) => ({
        id: `channel:${key}`,
        group: "vehicle",
        channelKey: key,
        channel,
      })),
      ...ptoReadings
        .filter(
          ([key]) =>
            !ptoActive ||
            !["ptoAnyEngaged", "ptoActiveTelltale", "eptoDriveEngagement"].includes(key)
        )
        .map(([key, reading]) => ({
          id: `pto:${key}`,
          group: "vehicle",
          ptoKey: key,
          reading,
        })),
    ],
    [gridChannelEntries, ptoReadings, ptoActive]
  );

  const orderedTelemetryCards = useMemo(
    () => applyTelemetryOrder(telemetryCards, telemetryOrder),
    [telemetryCards, telemetryOrder]
  );

  useEffect(() => {
    if (!id || telemetryCards.length === 0) return;

    const availableIds = new Set(telemetryCards.map((card) => card.id));
    const savedIds = telemetryOrder.filter((cardId) => availableIds.has(cardId));
    const savedIdSet = new Set(savedIds);
    const newIds = telemetryCards
      .map((card) => card.id)
      .filter((cardId) => !savedIdSet.has(cardId));
    const nextOrder = [...savedIds, ...newIds];

    if (JSON.stringify(nextOrder) !== JSON.stringify(telemetryOrder)) {
      setTelemetryOrder(nextOrder);
      localStorage.setItem(telemetryOrderStorageKey(id), JSON.stringify(nextOrder));
    }
  }, [id, telemetryCards]);

  function resetTelemetryLayout() {
    const defaultOrder = telemetryCards.map((card) => card.id);
    setTelemetryOrder(defaultOrder);
    localStorage.setItem(telemetryOrderStorageKey(id), JSON.stringify(defaultOrder));
  }

  function handleTelemetryDragEnd(result) {
    if (!result.destination) return;
    if (result.source.droppableId !== result.destination.droppableId) return;

    const group = result.source.droppableId;
    const groupCards = orderedTelemetryCards.filter((card) => card.group === group);
    const reorderedGroup = reorderItems(
      groupCards,
      result.source.index,
      result.destination.index
    );

    const reorderedIds = reorderedGroup.map((card) => card.id);
    const reorderedSet = new Set(reorderedIds);
    const nextOrder = [];
    let groupIndex = 0;

    orderedTelemetryCards.forEach((card) => {
      if (card.group === group) {
        nextOrder.push(reorderedIds[groupIndex]);
        groupIndex += 1;
      } else if (!reorderedSet.has(card.id)) {
        nextOrder.push(card.id);
      }
    });

    setTelemetryOrder(nextOrder);
    localStorage.setItem(telemetryOrderStorageKey(id), JSON.stringify(nextOrder));
  }

  const isLive = deviceValue?.last_updated
    ? moment(deviceValue.last_updated).isAfter(moment().subtract(LIVE_WINDOW_MINUTES, "minutes"))
    : false;
  const lastUpdatedLabel = deviceValue?.last_updated ? moment(deviceValue.last_updated).fromNow() : "No data yet";

  const momentStatus = (deviceValue?.moment_status || "unknown").toLowerCase();
  const lat = deviceValue?.position_latitude ?? deviceValue?.location?.latitude;
  const lng = deviceValue?.position_longitude ?? deviceValue?.location?.longitude;
  const speed = deviceValue?.convert_position_speed ?? deviceValue?.location?.speed_mph;
  const heading = deviceValue?.location?.heading ?? deviceValue?.position_direction ?? 0;
  const options = useMemo(
    () => ({
      icon: device?.latestWebhookData?.entity_address_item?.class_name ?? "fas fa-truck-container",
      heading,
      // Apply dynamic alert color to map marker/icon
      color: alertColor,
    }),
    [device?.latestWebhookData?.entity_address_item?.class_name, heading, alertColor]
  );

  const tank = deviceData?.tank ?? {};
  const transfer = deviceData?.transfer ?? {};
  const audit = deviceData?.audit ?? {};
  const diagnostics = deviceData?.diagnostics ?? {};

  const rs485Status = getRs485Status(deviceData, deviceValue);
  const hasRs485Status =
    rs485Status.connectionStatus !== "unknown" ||
    rs485Status.port !== "--" ||
    rs485Status.requestHex ||
    rs485Status.responseHex ||
    rs485Status.registerReads.length > 0;

  const rs485DefaultExpanded =
    rs485Status.connectionStatus !== "connected" ||
    rs485Status.crcValid === false ||
    Number(rs485Status.consecutiveFailures) > 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const transferMonitorOpen = searchParams.get("panel") === "transfers";
  const [businessDate, setBusinessDate] = useState(() => moment().format("YYYY-MM-DD"));

  const openTransferMonitor = () => {
    const next = new URLSearchParams(searchParams);
    next.set("panel", "transfers");
    setSearchParams(next, { replace: true });
  };

  const closeTransferMonitor = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("panel");
    setSearchParams(next, { replace: true });
  };

  const sensorOk = rs485Status.connectionStatus !== "error" && rs485Status.crcValid !== false;
  const transferDay = useTransferDay({ deviceId: id, from: businessDate, to: businessDate });
  const transferSession = useTransferSession({
    deviceId: id,
    deviceValue,
    deviceData,
    sensorOk,
    savedTransferId: transferDay.lastSaved?.transfer?.id,
  });

  const vehicleConnected = deviceValue?.vehicle?.connected;
  const activeFaults = deviceValue?.vehicle?.active_fault_count ?? diagnostics?.faults?.length ?? 0;

  const hasTelemetry = Boolean(deviceValue?.last_updated || Object.keys(channels).length);

  const mapDetails = {
    lastUpdated: deviceValue?.last_updated,
    motion: momentStatus,
    speed,
    levelPercent: deviceValue?.level_percent ?? channels?.tank_level?.current_value,
    volumeGallons: deviceValue?.volume_gallons ?? channels?.tank_volume?.current_value,
    capacityGallons: deviceValue?.capacity_gallons,
    gallonsRemaining: deviceValue?.gallons_remaining,
    pressurePsi: deviceValue?.pressure_psi ?? rs485Status?.pressurePsi,
    pressureMode: deviceValue?.pressure_mode ?? rs485Status?.pressureMode,
    jobState: currentJobState,
    transferActive: Boolean(transfer?.active),
    transferStartedAt: transfer?.startedAt ?? transfer?.started_at,
    activeAlertCount,
    activeFaultCount: activeFaults,
    ptoActive,
  };
  
  const surfaceSx = ({ palette, borders, functions: { rgba } }) => ({
    backgroundColor: palette.background?.card || palette.background.default,
    border: `1px solid ${rgba(palette.text.main, 0.12)}`,
    borderRadius: borders.borderRadius.lg,
    boxShadow: "none",
  });

  const nestedSurfaceSx = ({ palette, borders, functions: { rgba } }) => ({
    backgroundColor: rgba(palette.text.main, 0.035),
    border: `1px solid ${rgba(palette.text.main, 0.10)}`,
    borderRadius: borders.borderRadius.md,
    boxShadow: "none",
  });

  const SectionHeader = ({ title, subtitle, action }) => (
    <MDBox
      display="flex"
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      gap={2}
      mb={2.5}
      flexWrap="wrap"
    >
      <MDBox>
        <MDTypography variant="h6" color="text" fontWeight="bold">
          {title}
        </MDTypography>
        {subtitle && (
          <MDTypography variant="caption" color="text">
            {subtitle}
          </MDTypography>
        )}
      </MDBox>
      {action}
    </MDBox>
  );

  const StatusChip = ({ label, color = "default" }) => (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        height: 24,
        borderRadius: "6px",
        fontWeight: 700,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );

  if (loader) return <Loading transparent={true} />;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={{ xs: 2, md: 3 }}>
        {/* ================================================================
            DEVICE HEADER
            ================================================================ */}
        <Paper
          elevation={0}
          sx={(theme) => ({
            ...surfaceSx(theme),
            mb: 3,
            p: { xs: 2, md: 2.5 },
          })}
        >
          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            gap={2}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <MDBox minWidth={0}>
              <MDTypography
                variant="h4"
                // color={theme.palette.text.primary}
                fontWeight="bold"
                sx={{ wordBreak: "break-word"}}
              >
                {deviceData?.name || `Device #${id}`}
              </MDTypography>

              <MDBox
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={0.75}
                mt={0.5}
              >
                {deviceData?.asset_id && (
                  <MDTypography variant="caption" color="text">
                    Asset {deviceData.asset_id}
                  </MDTypography>
                )}

                {deviceData?.asset_id && deviceData?.source_hardware_id && (
                  <MDTypography variant="caption" color="text">
                    ·
                  </MDTypography>
                )}

                {deviceData?.source_hardware_id && (
                  <MDTypography variant="caption" color="text">
                    {deviceData.source_hardware_id}
                  </MDTypography>
                )}
              </MDBox>
            </MDBox>

            <MDBox
              display="flex"
              alignItems="center"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
              gap={1}
              flexWrap="wrap"
            >
              {hasTelemetry && (
                <MDTypography variant="caption" color="text" sx={{ mr: 1, opacity: 0.8 }}>
                  Last updated {lastUpdatedLabel}
                </MDTypography>
              )}

              {hasTelemetry && (
                <StatusChip
                  label={isLive ? "LIVE" : "STALE"}
                  color={isLive ? "success" : "error"}
                />
              )}

              {hasTelemetry && (
                <StatusChip
                  label={momentStatus.toUpperCase()}
                  color={MOTION_COLOR[momentStatus] ?? "default"}
                />
              )}

              {hasTelemetry && transfer?.active && (
                <Chip
                  label="TRANSFER IN PROGRESS"
                  color="info"
                  size="small"
                  onClick={openTransferMonitor}
                  sx={{ height: 24, borderRadius: "6px", fontWeight: 700, "& .MuiChip-label": { px: 1 }, cursor: "pointer" }}
                />
              )}

              <MDButton size="small" variant="outlined" color="info" onClick={openTransferMonitor}>
                <i className="fa-solid fa-right-left" style={{ marginRight: 8 }} />
                Transfers
              </MDButton>
            </MDBox>
          </MDBox>
        </Paper>

        <LiveTransferCard
          session={transferSession}
          lastSaved={transferDay.lastSaved}
          audit={deviceData?.audit}
          isLive={isLive}
          lastUpdated={deviceValue?.last_updated}
          capacity={deviceValue?.capacity_gallons}
          onOpenMonitor={openTransferMonitor}
        />

        {/* ================================================================
            MAIN DASHBOARD
            Desktop:
              ┌───────────────────────┬─────────────────────────────────┐
              │                       │ Operations Snapshot             │
              │       Tank            │ RS485 / Modbus                  │
              │                       │ Secondary Telemetry              │
              ├───────────────────────┴─────────────────────────────────┤
              │ Location                              │ Diagnostics      │
              └─────────────────────────────────────┴──────────────────┘
            Mobile: everything becomes one column.
            ================================================================ */}
        {hasTelemetry && (
          <Grid container spacing={3} alignItems="stretch">
            {/* ---------------------------- Top: TANK ------------------- */}
            {primaryChannelKey && (
              <>
                <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
                  <Paper
                    elevation={0}
                    sx={(theme) => ({
                      ...surfaceSx(theme),
                      width: "100%",
                      p: { xs: 2, md: 2.5 },
                      display: "flex",
                      flexDirection: "column",
                    })}
                  >
                    <SectionHeader
                      title={channels[primaryChannelKey]?.channel_name || "Tank Level"}
                      action={
                        <MDTypography variant="h3" color="text" fontWeight="bold">
                          {deviceValue?.level_percent ?? 0}%
                        </MDTypography>
                      }
                    />

                    <MDBox
                      flex={1}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight={{ xs: 200, md: 250 }}
                      py={0}
                    >
                      <MDBox sx={{ width: "100%", maxWidth: 340, height: 280 }}>
                        <TankVisual
                          shape={tank?.shape}
                          diameterVal={tank?.diameter_inches}
                          percent_level={deviceValue?.level_percent ?? 0}
                          displayColor={alertColor}
                          alertColor={alertColor}
                        />
                      </MDBox>
                    </MDBox>

                    <Grid container spacing={1} mt={1}>
                      <Grid item xs={6}>
                        <Paper
                          elevation={0}
                          sx={(theme) => ({
                            ...nestedSurfaceSx(theme),
                            p: 1.5,
                          })}
                        >
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={0}
                          >
                            Current Volume
                          </MDTypography>
                          <MDTypography variant="h4" color="text" fontWeight="bold">
                            {deviceValue?.volume_gallons ?? "--"}{" "}
                            <MDTypography component="span" variant="caption" color="text">
                              gal
                            </MDTypography>
                          </MDTypography>
                        </Paper>
                      </Grid>

                      <Grid item xs={6}>
                        <Paper
                          elevation={0}
                          sx={(theme) => ({
                            ...nestedSurfaceSx(theme),
                            p: 1.5,
                          })}
                        >
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={0}
                          >
                            Capacity
                          </MDTypography>
                          <MDTypography variant="h4" color="text" fontWeight="bold">
                            {deviceValue?.capacity_gallons ?? "--"}{" "}
                            <MDTypography component="span" variant="caption" color="text">
                              gal
                            </MDTypography>
                          </MDTypography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={(theme) => ({
                            ...nestedSurfaceSx(theme),
                            p: 1.5,
                          })}
                        >
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={0}
                          >
                            Remaining until full
                          </MDTypography>
                          <MDTypography variant="h5" color="text" fontWeight="bold">
                            {deviceValue?.gallons_remaining != null
                              ? `${deviceValue.gallons_remaining} gal`
                              : "--"}
                          </MDTypography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
                  <Paper
                  elevation={0}
                  sx={(theme) => ({
                    ...surfaceSx(theme),
                    width: "100%",
                    p: { xs: 2, md: 2.5 },
                  })}
                >
                  <SectionHeader
                    title="Location"
                    subtitle="Latest GPS position reported by the device"
                    action={
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="caption" color="text">
                          Current Speed
                        </MDTypography>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          {speed ? Number(speed).toFixed(2): 0} mph
                        </MDTypography>
                      </MDBox>
                    }
                  />

                  {lat != null && lng != null ? (
                    <MDBox
                      height={{ xs: 320, md: 400 }}
                      sx={{
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      <TabletMap lat={lat} lng={lng} moving={momentStatus === "moving"} levelPercent={deviceValue?.level_percent ?? channels?.tank_level?.current_value} volumeGallons={deviceValue?.volume_gallons ?? channels?.tank_volume?.current_value} options={options} site={site} details={mapDetails} />
                    </MDBox>
                  ) : (
                    <MDBox
                      height={{ xs: 240, md: 400 }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        background: "rgba(255,255,255,0.03)",
                        borderRadius: "10px",
                        border: "1px dashed rgba(255,255,255,0.12)",
                      }}
                    >
                      <MDTypography variant="body2" color="text">
                        No GPS fix reported.
                      </MDTypography>
                    </MDBox>
                  )}
                  </Paper>
                </Grid>
              </>
            )}

            {/* -------------------------- LOCATION ------------------------- */}
            {
              !primaryChannelKey &&
              <Grid item xs={12} lg={8} sx={{ display: "flex" }}>
                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    ...surfaceSx(theme),
                    width: "100%",
                    p: { xs: 2, md: 2.5 },
                  })}
                >
                  <SectionHeader
                    title="Location"
                    subtitle="Latest GPS position reported by the device"
                    action={
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="caption" color="text">
                          Current Speed
                        </MDTypography>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          {speed ? Number(speed).toFixed(2) : 0} mph
                        </MDTypography>
                      </MDBox>
                    }
                  />

                  {lat != null && lng != null ? (
                    <MDBox
                      height={{ xs: 320, md: 400 }}
                      sx={{
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      <TabletMap lat={lat} lng={lng} moving={momentStatus === "moving"} levelPercent={deviceValue?.level_percent ?? channels?.tank_level?.current_value} volumeGallons={deviceValue?.volume_gallons ?? channels?.tank_volume?.current_value} options={options} site={site} details={mapDetails} />
                    </MDBox>
                  ) : (
                    <MDBox
                      height={{ xs: 240, md: 400 }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        background: "rgba(255,255,255,0.03)",
                        borderRadius: "10px",
                        border: "1px dashed rgba(255,255,255,0.12)",
                      }}
                    >
                      <MDTypography variant="body2" color="text">
                        No GPS fix reported.
                      </MDTypography>
                    </MDBox>
                  )}
                </Paper>
              </Grid>
            }

            {/* ================================================================
            DEVICE INFORMATION
            ================================================================ */}
            <Grid
              item
              xs={12}
              lg={12}
            >
              <Accordion
                defaultExpanded={false}
                sx={(theme) => ({
                  ...surfaceSx(theme),
                  overflow: "hidden",
                  "&:before": { display: "none" },
                })}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="text" />}
                  sx={{ px: { xs: 2, md: 2.5 }, minHeight: 64 }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text" fontWeight="bold">
                        Device Information
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Hardware and installation metadata
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Device Name" value={deviceData?.name} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Device Type" value={device?.attributes?.DeviceType?.type_name || deviceData?.device_type?.name} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Brand" value={device?.attributes?.DeviceBrand?.brand_name || deviceData?.brand?.name} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Model" value={device?.attributes?.DeviceModel?.model_name || deviceData?.model?.name} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Item Type" value={deviceData?.item_type?.name} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Entity Address Item Id" value={device?.attributes?.EntityAddressItem?.address_item_id || deviceData?.entity_address_item_id} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Entity Address Id" value={device?.attributes?.EntityAddressItem?.address_id || deviceData?.entity_address_id} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Item Name" value={device?.attributes?.EntityAddressItem?.Item?.name ||  deviceData?.item_name} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Address Name" value={device?.attributes?.EntityAddressItem?.Address?.name || deviceData?.address_name} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Address" value={device?.attributes?.EntityAddressItem?.Address?.street_address1 || deviceData?.address} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FieldRow label="Status / Mode" value={device?.attributes?.StatusOption?.StatusType?.status_name ||deviceData?.status || deviceData?.mode} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* ================================================================
            EMPTY STATE
            ================================================================ */}
            {!hasTelemetry && (
              <Paper
                elevation={0}
                sx={(theme) => ({
                  ...surfaceSx(theme),
                  p: { xs: 3, md: 6 },
                })}
              >
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  gap={1}
                >
                  <SensorsOffIcon sx={{ fontSize: 64, color: "text" }} />
                  <MDTypography variant="h5" color="text">
                    No telemetry received yet
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    sx={{ maxWidth: 520, opacity: 0.8 }}
                  >
                    This device hasn't reported any tank, pressure, vehicle, or
                    location data. Confirm the tablet is powered on, signed in,
                    and connected before troubleshooting further.
                  </MDTypography>
                </MDBox>
              </Paper>
            )}

            {/* -------------------------- Bottom: OPERATIONS ---------------- */}
            <Grid
              item
              xs={12}
              lg={12}
              sx={{ display: "flex" }}
            >
              <MDBox width="100%" display="flex" flexDirection="column" gap={3}>
                {/* Operations Snapshot */}
                <Accordion
                  defaultExpanded={true}
                  sx={(theme) => ({
                    ...surfaceSx(theme),
                    width: "100%",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  })}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="text" />}
                    sx={{ px: { xs: 2, md: 2.5 }, minHeight: 64 }}
                  >
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <MDBox>
                        <MDTypography variant="h6" color="text" fontWeight="bold">
                          Operations Snapshot
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Current vehicle and transfer state
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={6} xl={3}>
                        <MetricCard
                          icon={<i className="fa-solid fa-gauge-high" style={{ color: alertColor }} />}
                          label="Pressure"
                          value={`${formatNumber(deviceValue?.pressure_psi, 0)} psi`}
                          helper={deviceValue?.pressure_mode ?? "No mode"}
                          color="info"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} xl={3}>
                        <MetricCard
                          icon={<i className="fa-solid fa-truck" style={{ color: alertColor }} />}
                          label="Motion"
                          value={momentStatus}
                          helper={`${formatNumber(speed, 0)} mph`}
                          color={momentStatus === "moving" ? "success" : "text"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} xl={3}>
                        <MetricCard
                          icon={<i className="fa-solid fa-right-left" style={{ color: alertColor }} />}
                          label="Transfer"
                          value={transfer?.active ? "Active" : "Inactive"}
                          helper={
                            transfer?.startedAt
                              ? moment(transfer.startedAt).fromNow()
                              : "No active transfer"
                          }
                          color={transfer?.active ? "info" : "text"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} xl={3}>
                        <MetricCard
                          icon={<i className="fa-solid fa-link" style={{ color: alertColor }} />}
                          label="Vehicle Link"
                          value={
                            vehicleConnected == null
                              ? "--"
                              : vehicleConnected
                                ? "Connected"
                                : "Disconnected"
                          }
                          helper={`${activeFaults} active fault${
                            Number(activeFaults) === 1 ? "" : "s"
                          }`}
                          color={vehicleConnected ? "success" : "warning"}
                        />
                      </Grid>
                      {ptoActive && (
                        <Grid item xs={12} sm={6} md={6} xl={3}>
                          <MetricCard
                            icon={<i className="fa-solid fa-gears" style={{ color: alertColor }} />}
                            label="PTO Drive"
                            value="Engaged"
                            helper="Active"
                            color="success"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* RS485 / Modbus */}
                {hasRs485Status && (
                  <Accordion
                    defaultExpanded={rs485DefaultExpanded}
                    sx={(theme) => ({
                      ...surfaceSx(theme),
                      overflow: "hidden",
                      "&:before": { display: "none" },
                    })}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon color="text" />}
                      sx={{ px: { xs: 2, md: 2.5 }, minHeight: 64 }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        gap={2}
                        pr={1}
                      >
                        <MDBox>
                          <MDTypography variant="h6" color="text" fontWeight="bold">
                            RS485 / Modbus Status
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Serial communication and register health
                          </MDTypography>
                        </MDBox>

                        <StatusChip
                          label={String(rs485Status.connectionStatus).toUpperCase()}
                          color={
                            rs485Status.connectionStatus === "connected"
                              ? "success"
                              : "warning"
                          }
                        />
                      </MDBox>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5 }}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow label="Port" value={rs485Status.port} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow
                            label="Serial"
                            value={`${rs485Status.baudRate} ${rs485Status.dataBits}${rs485Status.parity}${rs485Status.stopBits}`}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow
                            label="Active slave"
                            value={rs485Status.activeSlaveAddress}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow
                            label="Failures"
                            value={rs485Status.consecutiveFailures}
                            valueColor={
                              Number(rs485Status.consecutiveFailures) > 0
                                ? "warning"
                                : undefined
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow
                            label="CRC valid"
                            value={
                              rs485Status.crcValid == null
                                ? "--"
                                : rs485Status.crcValid
                                  ? "Yes"
                                  : "No"
                            }
                            valueColor={
                              rs485Status.crcValid === false ? "error" : undefined
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow
                            label="Pressure"
                            value={`${formatNumber(rs485Status.pressurePsi, 0)} psi`}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow
                            label="Pressure mode"
                            value={rs485Status.pressureMode ?? "--"}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FieldRow
                            label="Last success"
                            value={
                              rs485Status.lastSuccessfulAt
                                ? moment(rs485Status.lastSuccessfulAt).fromNow()
                                : "--"
                            }
                          />
                        </Grid>
                      </Grid>

                      {(rs485Status.requestHex || rs485Status.responseHex) && (
                        <Paper
                          elevation={0}
                          sx={(theme) => ({
                            ...nestedSurfaceSx(theme),
                            mt: 2,
                            p: 1.75,
                          })}
                        >
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={0.75}
                          >
                            Raw request / response
                          </MDTypography>
                          <MDTypography variant="body2" color="text">
                            TX: {rs485Status.requestHex ?? "--"}
                          </MDTypography>
                          <MDTypography variant="body2" color="text">
                            RX: {rs485Status.responseHex ?? "--"}
                          </MDTypography>
                        </Paper>
                      )}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Secondary Telemetry */}
                <Accordion
                  defaultExpanded={false}
                  sx={(theme) => ({
                    ...surfaceSx(theme),
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  })}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="text" />}
                    sx={{ px: { xs: 2, md: 2.5 }, minHeight: 64 }}
                  >
                    <SectionHeader
                      title="Secondary Telemetry"
                      subtitle="Additional channels and PTO readings"
                      action={
                        <Tooltip title="Reset telemetry card order">
                          <IconButton onClick={resetTelemetryLayout} size="small" aria-label="Reset telemetry card order">
                            <RestartAltIcon style={{ color: alertColor }} />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5 }}>
                    <DragDropContext onDragEnd={handleTelemetryDragEnd}>
                      <Droppable droppableId="vehicle" direction="horizontal">
                        {(provided) => (
                          <MDBox
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            display="grid"
                            gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))"
                            gap={1.5}
                          >
                            {orderedTelemetryCards.map((card, index) => (
                              <Draggable key={card.id} draggableId={card.id} index={index}>
                                {(providedDrag, snapshot) => (
                                  <MDBox
                                    ref={providedDrag.innerRef}
                                    {...providedDrag.draggableProps}
                                    sx={{
                                      opacity: snapshot.isDragging ? 0.82 : 1,
                                      transform: snapshot.isDragging ? "scale(1.02)" : undefined,
                                    }}
                                  >
                                    <MDBox
                                      {...providedDrag.dragHandleProps}
                                      sx={{ position: "relative" }}
                                      aria-label={`Reorder ${card.id}`}
                                    >
                                      <MDBox
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          right: 8,
                                          zIndex: 1,
                                          color: "text.secondary",
                                        }}
                                      >
                                        <DragIndicatorIcon fontSize="small" style={{ color: alertColor }} />
                                      </MDBox>

                                      {card.channel ? (
                                        <ChannelGauge
                                          channelKey={card.channelKey}
                                          channel={card.channel}
                                          compact
                                          displayColor={alertColor}
                                        />
                                      ) : (
                                        <MetricCard
                                          icon={<i className="fa-solid fa-gears" style={{ color: alertColor }} />}
                                          label={card.reading.label}
                                          value={
                                            card.ptoKey.toLowerCase().includes("telltale") ||
                                            card.ptoKey.toLowerCase().includes("engagement") ||
                                            card.ptoKey.toLowerCase().includes("status")
                                              ? formatPtoStatus(card.reading.value)
                                              : `${formatNumber(card.reading.value, 0)}${
                                                  card.reading.unit ? ` ${card.reading.unit}` : ""
                                                }`
                                          }
                                          color={
                                            card.ptoKey === "eptoSystemError" &&
                                            Number(card.reading.value) === 1
                                              ? "error"
                                              : "text"
                                          }
                                        />
                                      )}
                                    </MDBox>
                                  </MDBox>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </MDBox>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </AccordionDetails>
                </Accordion>
              </MDBox>
            </Grid>

            {/* -------------------------- DIAGNOSTICS ---------------------- */}
            <Grid item xs={12} lg={primaryChannelKey ? 12 : 4} sx={{ display: "flex" }}>
              <MDBox width="100%" display="flex" flexDirection="column" gap={3}>
                <Accordion
                  defaultExpanded={false}
                  sx={(theme) => ({
                    ...surfaceSx(theme),
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  })}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="text" />}
                    sx={{ px: { xs: 2, md: 2.5 }, minHeight: 64 }}
                  >
                    <SectionHeader
                      title="Diagnostics"
                      subtitle="Communication, faults and audit state"
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5 }}>
                    <MDBox display="flex" flexDirection="column" gap={0}>
                      <FieldRow
                        label="RS485 port"
                        value={diagnostics?.rs485?.port ?? "--"}
                      />

                      <FieldRow
                        label="RS485 status"
                        value={diagnostics?.rs485?.connection_status ?? "--"}
                        valueColor={
                          diagnostics?.rs485?.connection_status === "connected"
                            ? "success"
                            : "error"
                        }
                      />

                      <FieldRow
                        label="Consecutive failures"
                        value={diagnostics?.rs485?.consecutive_failures ?? 0}
                      />

                      <FieldRow
                        label="Active faults"
                        value={(diagnostics?.faults ?? []).length}
                      />

                      <FieldRow
                        label="Audit severity"
                        value={audit?.severity ?? "none"}
                        valueColor={
                          audit?.severity === "none" ? "text" : "warning"
                        }
                      />

                      {audit?.reviewRequired && (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 2,
                            p: 1.5,
                            background: "rgba(255,165,0,0.08)",
                            border: "1px solid rgba(255,165,0,0.25)",
                            borderRadius: "8px",
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            color="warning"
                            fontWeight="bold"
                          >
                            Manual review required
                          </MDTypography>
                        </Paper>
                      )}
                    </MDBox>
                  </AccordionDetails>
                </Accordion>
              </MDBox>
            </Grid>
          </Grid>
        )}

        {/* ================================================================
            WORKSPACE / LOGS / ALERTS / EVENTS / RULES / SERVICE REQUESTS
            ================================================================ */}
        <MDBox mt={3}>
          <TabletWorkspace
            id={id}
            device={device}
            deviceData={deviceData}
            deviceValue={deviceValue}
          />
        </MDBox>
      </MDBox>

      <Footer />
      <TransferMonitorDialog
        open={transferMonitorOpen}
        onClose={closeTransferMonitor}
        deviceName={deviceData?.name || `Device #${id}`}
        assetId={deviceData?.asset_id}
        deviceValue={deviceValue}
        deviceData={deviceData}
        rs485Status={rs485Status}
        isLive={isLive}
        session={transferSession}
        day={{
          date: businessDate,
          transfers: transferDay.transfers,
          summary: transferDay.summary,
          status: transferDay.status,
          errorCode: transferDay.errorCode,
          refresh: transferDay.refresh,
          loadMore: transferDay.loadMore,
          nextCursor: transferDay.nextCursor,
        }}
        setBusinessDate={setBusinessDate}
        lastUpdatedLabel={lastUpdatedLabel}
      />
      </DashboardLayout>
    );
  };

export default TabletDevice;